import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { SortEvent } from 'primeng/api';
import { CheckboxChangeEvent } from 'primeng/checkbox';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { Roll } from '@app/models/knitting/roll';
import { KnittingMachineRollsService } from '@app/services/knitting-machine-rolls.service';

@Component({
  selector: 'app-sidebar-knitting-programming',
  templateUrl: './sidebar-knitting-programming.component.html',
  styleUrl: './sidebar-knitting-programming.component.scss',
})
export class SidebarKnittingProgrammingComponent implements OnInit {
  @Input() selectedComboKeys: Set<string> = new Set();
  @Output() closeModal = new EventEmitter<void>();

  isLoading = 0;
  selectedRollIds = new Set<string>();
  disableGenerateButton = false;
  selectedRolls: Roll[] = [];
  limitedSize = 600;

  sortField?: string = 'number';
  sortOrder = 1; // 1 = asc, -1 = desc

  constructor(private knittingMachineRollsService: KnittingMachineRollsService) {}

  ngOnInit() {
    if (!this.selectedComboKeys || this.selectedComboKeys.size === 0) {
      GlobalMessageUtil.warn('Warning', 'No combo keys provided. Please select valid combos.');
      return;
    }
  }

  onSort(event: SortEvent) {
    this.sortField = event.field;
    this.sortOrder = event.order || 1;
  }

  get rolls(): Roll[] {
    const allRolls: Roll[] = [];

    this.selectedComboKeys.forEach((comboKey) => {
      const rolls = this.knittingMachineRollsService.getRollsByComboKey(comboKey);
      allRolls.push(...rolls);
    });

    const uniqueRolls = allRolls.filter((roll, index, self) => index === self.findIndex((r) => r.id === roll.id));

    return this.sortRolls(uniqueRolls);
  }

  // Método para ordenar los rollos
  private sortRolls(rolls: Roll[]): Roll[] {
    return [...rolls].sort((a, b) => {
      const valueA = a[this.sortField as keyof Roll];
      const valueB = b[this.sortField as keyof Roll];

      if (this.sortOrder) {
        if (valueA == null) return 1 * this.sortOrder;
        if (valueB == null) return -1 * this.sortOrder;

        if (typeof valueA === 'string' && typeof valueB === 'string') {
          return valueA.localeCompare(valueB) * this.sortOrder;
        }

        if (valueA < valueB) return -1 * this.sortOrder;
        if (valueA > valueB) return 1 * this.sortOrder;
      }

      return 0;
    });
  }

  toggleRollSelection(rollId: string, event: CheckboxChangeEvent) {
    const checked = event.checked;

    if (checked) {
      if (this.selectedRolls.length >= this.limitedSize) {
        console.warn('⚠️ Solo puedes seleccionar hasta 600 rolls');
        GlobalMessageUtil.warn('Error', '⚠️ A maximum of 600 rolls can be printed!');
        return; // no se agrega el 601
      }

      this.selectedRollIds.add(rollId);

      const roll = this.rolls.find((r) => r.number === rollId);
      if (roll && !this.selectedRolls.some((r) => r.number === rollId)) {
        this.selectedRolls = [...this.selectedRolls, roll];
      }
    } else {
      this.selectedRollIds.delete(rollId);
      this.selectedRolls = this.selectedRolls.filter((r) => r.number !== rollId);
    }
  }

  trackByRollNumber(index: number, roll: Roll): string {
    return roll.number ?? '';
  }

  printSelectedBarcodes() {
    if (this.selectedRollIds.size === 0) {
      GlobalMessageUtil.warn('No selection', 'Please select rolls to print');
      return;
    }

    this.isLoading++;
    const selectedRollNumbers = Array.from(this.selectedRollIds);

    this.knittingMachineRollsService.printRollsFromMultipleCombos(selectedRollNumbers, this.selectedComboKeys).subscribe({
      next: (result) => {
        this.isLoading--;
        if (result) {
          this.selectedRollIds.clear();
          GlobalMessageUtil.success('Success', 'Barcodes printed successfully');
        } else {
          GlobalMessageUtil.error('Error', 'An error occurred while printing the barcodes. Please try again later.');
        }
      },
      error: (error) => {
        this.isLoading--;
        console.error('Print error:', error);
        GlobalMessageUtil.error('Error', 'An error occurred while printing the barcodes. Please try again later.');
      },
    });
  }

  isAllSelected(): boolean {
    const selectableRolls = this.rolls.filter((r) => r.status !== 2);
    return selectableRolls.length > 0 && selectableRolls.every((r) => this.selectedRollIds.has(r.number ?? ''));
  }

  toggleAllSelection(event: CheckboxChangeEvent) {
    const checked = event.checked;

    this.selectedRollIds.clear();
    this.selectedRolls = [];

    if (checked) {
      // Filtrar rolls seleccionables (no status 2)
      const selectableRolls = this.rolls.filter((r) => r.status !== 2);

      // Cortar a máximo 80
      const limited = selectableRolls.slice(0, this.limitedSize);

      limited.forEach((r) => {
        if (r.number) {
          this.selectedRollIds.add(r.number);
        }
      });

      this.selectedRolls = [...limited];
    }
  }

  deleteRoll(id: string) {
    this.knittingMachineRollsService.deleteRoll(id);

    // Remove from local selection if it was selected
    const rollToDelete = this.rolls.find((r) => r.id === id);
    if (rollToDelete && this.selectedRollIds.has(rollToDelete.number!)) {
      this.selectedRollIds.delete(rollToDelete.number!);
    }
  }

  deleteRollsByBatch() {
    if (this.selectedRollIds.size === 0) {
      GlobalMessageUtil.warn('No selection', 'Please select rolls to delete');
      return;
    }

    const rollNumbers = Array.from(this.selectedRollIds);
    this.isLoading++;

    this.knittingMachineRollsService.deleteRollsByBatch(rollNumbers).subscribe({
      next: () => {
        this.isLoading--;
        this.selectedRollIds.clear();

        if (this.rolls.length === 0) {
          this.closeModal.emit();
        }
      },
      error: (error) => {
        this.isLoading--;
        console.error('Delete error:', error);
        GlobalMessageUtil.error('Error', 'Failed to delete selected rolls');
      },
    });
  }

  isRollProduced(roll: Roll): boolean {
    if (!roll.stationTimeStamps || roll.stationTimeStamps.length === 0) {
      return false;
    }

    const validTimeStamps = roll.stationTimeStamps
      .filter((st) => st.timeStamp != null)
      .sort((a, b) => new Date(a.timeStamp!).getTime() - new Date(b.timeStamp!).getTime());

    return validTimeStamps.length > 0;
  }

  onClose() {
    this.closeModal.emit();
  }

  onSelectionChange(selection: []) {
    if (selection.length > this.limitedSize) {
      // Si pasa de 600, revertimos al estado anterior
      this.selectedRolls = this.selectedRolls.slice(0, this.limitedSize);
      console.warn('⚠️ Máximo 600 rolls permitidos');
      GlobalMessageUtil.warn('Error', '⚠️ A maximum of 600 rolls can be printed!');
    } else {
      this.selectedRolls = selection;
    }

    this.selectedRollIds.clear();
    this.selectedRolls.forEach((roll) => {
      if (roll.number) {
        this.selectedRollIds.add(roll.number);
      }
    });
  }
}
